import { useState, useRef, useEffect, useCallback } from 'react';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { LoginPromptModal } from './ui/LoginPromptModal';
import { Tooltip } from './ui/Tooltip';

interface AddToItineraryButtonProps {
  venueId: string;
  venueName: string;
  className?: string;
}

const MapPlusIcon = ({ className = '' }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    {/* Folded map: three panels with folds, slightly tilted creases */}
    <path d="M3 6.5 9 4l6 2.5 6-2.5v13l-6 2.5-6-2.5-6 2.5v-13Z" />
    <path d="M9 4v13" />
    <path d="M15 6.5v13" />
    {/* Plus badge bottom-right, in its own little disk so it reads as an action */}
    <circle cx="18.5" cy="18.5" r="3.25" fill="currentColor" stroke="none" />
    <path d="M18.5 16.9v3.2M16.9 18.5h3.2" stroke="var(--add-itin-plus-stroke, #FAF9F6)" strokeWidth={1.6} />
  </svg>
);

export const AddToItineraryButton = ({
  venueId,
  venueName,
  className = '',
}: AddToItineraryButtonProps) => {
  const { user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const newTitleInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  // When set, popover swaps to a day-picker for that itinerary.
  const [dayPickerFor, setDayPickerFor] = useState<{
    id: string;
    title: string;
    days: { _id: string; ordinal: number; title?: string | null; stops: unknown[] }[];
  } | null>(null);
  const utils = trpc.useUtils();

  const { data: listData, isLoading } = trpc.itinerary.listMine.useQuery(
    { limit: 50, offset: 0, includeArchived: false },
    { enabled: !!user && isOpen }
  );

  const addStop = trpc.itinerary.addStop.useMutation();
  const createItin = trpc.itinerary.create.useMutation();

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closePopover();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Esc to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePopover();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Autofocus title input when the new-itinerary form opens
  useEffect(() => {
    if (showNewForm) newTitleInputRef.current?.focus();
  }, [showNewForm]);

  const closePopover = () => {
    setIsOpen(false);
    setShowNewForm(false);
    setNewTitle('');
    setConfirmation(null);
    setErrorMsg(null);
    setDayPickerFor(null);
  };

  const flashConfirmAndClose = (msg: string) => {
    setConfirmation(msg);
    setTimeout(() => closePopover(), 1200);
  };

  const handleAddToExisting = useCallback(
    async (itinId: string, dayId: string, itinTitle: string) => {
      setErrorMsg(null);
      try {
        await addStop.mutateAsync({
          itineraryId: itinId,
          dayId,
          refType: 'venue',
          venueId,
        });
        utils.itinerary.listMine.invalidate();
        utils.itinerary.getBySlug.invalidate();
        flashConfirmAndClose(`Added to "${itinTitle}"`);
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Could not add to itinerary');
      }
    },
    [addStop, utils, venueId]
  );

  const handleCreateAndAdd = useCallback(async () => {
    const trimmed = newTitle.trim();
    if (!trimmed) {
      setErrorMsg('Give the itinerary a title');
      return;
    }
    setErrorMsg(null);
    try {
      const created = await createItin.mutateAsync({ title: trimmed, regionRefs: [] });
      const firstDayId = created.days[0]?._id;
      if (!firstDayId) {
        setErrorMsg('New itinerary missing a day — try again');
        return;
      }
      await addStop.mutateAsync({
        itineraryId: created._id,
        dayId: firstDayId,
        refType: 'venue',
        venueId,
      });
      utils.itinerary.listMine.invalidate();
      flashConfirmAndClose(`Created "${trimmed}" and added`);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Could not create itinerary');
    }
  }, [newTitle, createItin, addStop, utils, venueId]);

  const items = listData?.items ?? [];
  const pending = addStop.isPending || createItin.isPending;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Tooltip label="Add to an itinerary.">
        <button
          onClick={() => {
            if (!user) {
              setShowLoginPrompt(true);
              return;
            }
            setIsOpen((v) => !v);
          }}
          className="inline-flex items-center justify-center rounded-full p-2 transition-colors duration-150 text-gray-500 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
          aria-label={`Add ${venueName} to itinerary`}
          aria-haspopup="menu"
          aria-expanded={isOpen}
        >
          <MapPlusIcon className="h-6 w-6" />
        </button>
      </Tooltip>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-80 origin-top-right rounded-lg border border-stone-200 bg-cream-50 shadow-lg ring-1 ring-black/[0.04] z-30"
          style={{ backgroundColor: '#FAF9F6' }}
        >
          <div className="px-3 py-2 border-b border-stone-200/70">
            <p className="font-serif text-base text-stone-900">Add to itinerary</p>
            <p className="text-xs text-stone-500 mt-0.5 truncate">{venueName}</p>
          </div>

          {confirmation ? (
            <div className="px-3 py-3 text-sm text-emerald-800 bg-emerald-50/60">
              {confirmation}
            </div>
          ) : dayPickerFor ? (
            <>
              <div className="px-2 py-1.5 border-b border-stone-200/70 flex items-center gap-1">
                <button
                  onClick={() => {
                    setDayPickerFor(null);
                    setErrorMsg(null);
                  }}
                  disabled={pending}
                  className="p-1 rounded hover:bg-stone-100/70 text-stone-500 hover:text-stone-800"
                  aria-label="Back to itineraries"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-stone-500 leading-tight">Choose a day in</p>
                  <p className="font-serif text-sm text-stone-900 truncate leading-tight">{dayPickerFor.title}</p>
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto py-1">
                {[...dayPickerFor.days]
                  .sort((a, b) => a.ordinal - b.ordinal)
                  .map((day) => {
                    const stopCount = day.stops?.length ?? 0;
                    return (
                      <button
                        key={day._id}
                        disabled={pending}
                        onClick={() =>
                          handleAddToExisting(dayPickerFor.id, day._id, dayPickerFor.title)
                        }
                        className="w-full text-left px-3 py-2 text-sm hover:bg-stone-100/70 disabled:opacity-50 flex items-center justify-between gap-3"
                      >
                        <span className="font-serif text-stone-900">
                          Day {day.ordinal + 1}
                          {day.title ? (
                            <span className="text-stone-500 font-sans"> · {day.title}</span>
                          ) : null}
                        </span>
                        <span className="text-xs text-stone-500 shrink-0">
                          {stopCount} stop{stopCount === 1 ? '' : 's'}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </>
          ) : (
            <>
              <div className="max-h-64 overflow-y-auto py-1">
                {isLoading ? (
                  <p className="px-3 py-2 text-sm text-stone-500">Loading…</p>
                ) : items.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-stone-500">
                    You don't have any itineraries yet.
                  </p>
                ) : (
                  items.map((itin) => {
                    const firstDayId = itin.days[0]?._id;
                    const stopCount = itin.days.reduce(
                      (n, d) => n + (d.stops?.length ?? 0),
                      0
                    );
                    const isMultiDay = itin.days.length > 1;
                    return (
                      <button
                        key={itin._id}
                        disabled={!firstDayId || pending}
                        onClick={() => {
                          if (!firstDayId) return;
                          if (isMultiDay) {
                            setDayPickerFor({
                              id: itin._id,
                              title: itin.title,
                              days: itin.days.map((d) => ({
                                _id: d._id,
                                ordinal: d.ordinal,
                                title: d.title ?? null,
                                stops: d.stops ?? [],
                              })),
                            });
                          } else {
                            handleAddToExisting(itin._id, firstDayId, itin.title);
                          }
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-stone-100/70 disabled:opacity-50 flex items-center justify-between gap-3"
                      >
                        <span className="truncate font-serif text-stone-900">{itin.title}</span>
                        <span className="text-xs text-stone-500 shrink-0 flex items-center gap-1">
                          {itin.days.length}d · {stopCount} stop{stopCount === 1 ? '' : 's'}
                          {isMultiDay && (
                            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="M9 6l6 6-6 6" />
                            </svg>
                          )}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="border-t border-stone-200/70">
                {!showNewForm ? (
                  <button
                    onClick={() => setShowNewForm(true)}
                    disabled={pending}
                    className="w-full text-left px-3 py-2 text-sm text-primary-800 hover:bg-primary-50/60 flex items-center gap-2"
                  >
                    <span className="text-lg leading-none">+</span>
                    <span>New itinerary…</span>
                  </button>
                ) : (
                  <div className="px-3 py-2 space-y-2">
                    <input
                      ref={newTitleInputRef}
                      type="text"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateAndAdd();
                      }}
                      placeholder="Itinerary title"
                      maxLength={120}
                      className="w-full rounded border border-stone-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setShowNewForm(false);
                          setNewTitle('');
                          setErrorMsg(null);
                        }}
                        disabled={pending}
                        className="px-2 py-1 text-xs text-stone-600 hover:text-stone-900"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateAndAdd}
                        disabled={pending || !newTitle.trim()}
                        className="px-2.5 py-1 text-xs rounded bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50"
                      >
                        {pending ? 'Creating…' : 'Create & add'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {errorMsg && (
                <div className="px-3 py-2 text-xs text-red-700 bg-red-50/60 border-t border-red-100">
                  {errorMsg}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        feature="Itinerary planning"
      />
    </div>
  );
};
